import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Search, ArrowRight, QrCode, Store, Smartphone, Zap, BadgeCheck, Apple, Play } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api, img } from "@/lib/api";
import { useLoc } from "@/lib/location";
import { money } from "@/lib/format";
import { Navbar, Footer } from "@/components/site/Navbar";
import { DealCard } from "@/components/site/DealCard";
import { CategoryIcon } from "@/components/site/bits";
import { LocationPicker } from "@/components/site/LocationPicker";

const HERO = "https://images.unsplash.com/photo-1550989460-0adf9ea622e2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export function DealRow({ title, sub, params, testid, link }) {
  const { loc } = useLoc();
  const [deals, setDeals] = useState(null);
  const key = JSON.stringify(params) + loc.lat + loc.lng;
  useEffect(() => {
    api.get("/deals", { params: { lat: loc.lat, lng: loc.lng, limit: 8, ...params } }).then((r) => setDeals(r.data)).catch(() => setDeals([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return (
    <section data-testid={testid} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-end justify-between mb-5">
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{title}</h2>
          {sub && <p className="text-sm text-slate-500 mt-1">{sub}</p>}
        </div>
        {link && <Link to={link} className="text-sm font-bold text-red-500 flex items-center gap-1" data-testid={`${testid}-see-all`}>See all <ArrowRight className="w-4 h-4" /></Link>}
      </div>
      {deals === null ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">{[0, 1, 2, 3].map((i) => <div key={i} className="h-80 rounded-2xl bg-slate-100 animate-pulse" />)}</div>
      ) : deals.length === 0 ? (
        <p className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-2xl p-8 text-center">No live deals here right now. Try a wider radius or another location.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">{deals.map((d, i) => <DealCard key={d.id} deal={d} index={i} />)}</div>
      )}
    </section>
  );
}

function Hero() {
  const { loc } = useLoc();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  return (
    <section className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 space-y-6 animate-rise">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-red-500 to-orange-500 text-white"><Zap className="w-3.5 h-3.5 fill-white" /> Flash deals near you</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900">
            Discover nearby deals.<br /><span className="text-red-500">Claim in seconds.</span><br />Save locally.
          </h1>
          <p className="text-base text-slate-600 max-w-lg">Time-limited offers from supermarkets, cafés, salons and stores around the corner. Claim a coupon, walk in, show your QR code.</p>
          <div className="bg-white border border-slate-200 rounded-2xl shadow-lg p-2 flex flex-col sm:flex-row gap-2 max-w-2xl">
            <button data-testid="hero-location-button" onClick={() => setOpen(true)} className="flex items-center gap-2 px-4 h-12 rounded-xl bg-slate-50 hover:bg-slate-100 text-sm font-semibold sm:max-w-[220px]">
              <MapPin className="w-4 h-4 text-red-500 shrink-0" /><span className="truncate">{loc.label}</span>
            </button>
            <form onSubmit={(e) => { e.preventDefault(); nav(`/deals?q=${encodeURIComponent(q)}`); }} className="flex flex-1 gap-2">
              <div className="flex-1 flex items-center gap-2 px-3"><Search className="w-4 h-4 text-slate-400" /><input data-testid="hero-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Groceries, coffee, haircut…" className="w-full h-12 outline-none text-sm" /></div>
              <button data-testid="hero-search-button" className="px-6 h-12 rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold active:scale-95 transition-transform">Find deals</button>
            </form>
          </div>
        </div>
        <div className="lg:col-span-5 relative hidden sm:block">
          <div className="absolute -inset-4 bg-gradient-to-br from-orange-200 via-red-100 to-transparent rounded-[2.5rem] rotate-3" />
          <img src={HERO} alt="Fresh produce at a local market" className="relative rounded-[2rem] shadow-2xl aspect-[4/3] object-cover w-full" />
          <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 border border-slate-100">
            <QrCode className="w-9 h-9 text-slate-900" /><div><p className="text-xs text-slate-500">Your coupon code</p><p className="font-mono text-xl font-extrabold tracking-[0.3em]">05832</p></div>
          </div>
        </div>
      </div>
      <LocationPicker open={open} onOpenChange={setOpen} />
    </section>
  );
}

function Categories() {
  const [cats, setCats] = useState([]);
  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
  }, []);
  return (
    <section id="categories" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-5">Shop by category</h2>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {cats.map((c, i) => (
          <Link key={c.id} to={`/category/${c.slug}`} data-testid={`category-tile-${c.slug}`}
            className={`group rounded-2xl p-4 flex flex-col gap-3 border border-slate-200 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] ${i % 3 === 0 ? "bg-red-50" : i % 3 === 1 ? "bg-amber-50" : "bg-emerald-50"}`}>
            <span className="w-11 h-11 rounded-xl bg-white grid place-items-center shadow-sm group-hover:scale-110 transition-transform"><CategoryIcon name={c.icon} className="w-5 h-5 text-red-500" /></span>
            <span className="font-heading font-bold text-sm text-slate-900">{c.name}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function FeaturedMerchants() {
  const [ms, setMs] = useState([]);
  useEffect(() => {
    api.get("/merchants/featured").then((r) => setMs(r.data));
  }, []);
  if (!ms.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="featured-merchants">
      <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-5">Featured merchants</h2>
      <div className="flex gap-4 overflow-x-auto pb-2">
        {ms.map((m) => (
          <Link key={m.id} to={`/deals?merchant=${m.id}`} data-testid={`featured-merchant-${m.id}`} className="min-w-[220px] bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-3 hover:shadow-md transition-shadow">
            {m.logo ? <img src={img(m.logo)} alt="" className="w-12 h-12 rounded-xl object-cover" /> : <span className="w-12 h-12 rounded-xl bg-slate-900 text-white grid place-items-center font-bold">{m.business_name[0]}</span>}
            <div><p className="font-bold text-sm text-slate-900">{m.business_name}</p><p className="text-xs text-slate-500">{m.city} · {m.live_deals} live deals</p></div>
          </Link>
        ))}
      </div>
    </section>
  );
}

const STEPS = [
  { icon: MapPin, t: "Set your location", d: "Use GPS or search your area and pick a radius from 500 m to 50 km." },
  { icon: Zap, t: "Claim a flash deal", d: "Tap Claim Deal to instantly get a QR coupon and a 5-digit code." },
  { icon: Store, t: "Visit within 24 hours", d: "Walk into the participating branch before the coupon countdown ends." },
  { icon: BadgeCheck, t: "Scan & save", d: "Staff scans your QR or enters your code. Your savings are verified." },
];

function HowItWorks() {
  return (
    <section className="bg-slate-950 text-white mt-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-10">How SuperDeals works</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s, i) => (
            <div key={s.t} className="border border-slate-800 rounded-2xl p-6 bg-slate-900/60">
              <span className="font-mono text-amber-400 text-xs">0{i + 1}</span>
              <s.icon className="w-7 h-7 text-red-400 my-3" />
              <p className="font-heading font-bold text-lg">{s.t}</p>
              <p className="text-sm text-slate-400 mt-1">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Packages() {
  const [pkgs, setPkgs] = useState([]);
  useEffect(() => {
    api.get("/packages").then((r) => setPkgs(r.data));
  }, []);
  return (
    <section id="packages" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="grid lg:grid-cols-12 gap-8 items-end mb-8">
        <div className="lg:col-span-7">
          <span className="text-xs font-bold uppercase tracking-wider text-red-500">Become a seller</span>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mt-2">Bring verified walk-ins to every branch</h2>
          <p className="text-slate-600 mt-2">Publish branch-specific flash deals, verify customers with QR or 5-digit codes, and track real store visits. Packages are billed by simultaneous active deal slots.</p>
        </div>
        <div className="lg:col-span-5 lg:text-right"><Link data-testid="packages-register-button" to="/seller/register" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-red-500 hover:bg-red-600 text-white font-bold">Register your store <ArrowRight className="w-4 h-4" /></Link></div>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-2xl">
        <table className="w-full text-sm min-w-[640px]" data-testid="packages-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr><th className="p-4">Package</th><th className="p-4">Active deals</th><th className="p-4">Activation</th><th className="p-4">Monthly</th><th className="p-4">6 months</th><th className="p-4">Annual</th></tr>
          </thead>
          <tbody>
            {pkgs.map((p) => (
              <tr key={p.id} className={`border-t border-slate-100 ${p.code === "growth" ? "bg-red-50/60" : ""}`}>
                <td className="p-4 font-heading font-bold text-slate-900">{p.name}{p.code === "growth" && <span className="ml-2 text-[10px] bg-red-500 text-white px-1.5 py-0.5 rounded-full">POPULAR</span>}</td>
                <td className="p-4 font-bold">{p.slots}</td><td className="p-4">{money(p.activation_price)}</td><td className="p-4">{money(p.monthly_price)}</td>
                <td className="p-4">{money(p.six_month_price)}<span className="block text-[11px] text-emerald-600">1 month free</span></td>
                <td className="p-4">{money(p.annual_price)}<span className="block text-[11px] text-emerald-600">2 months free</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500 mt-3">Initial activation includes 1 month of validity. Online payments launch soon — for now, our team activates packages manually.</p>
    </section>
  );
}

function AppsAndFaq() {
  const [faqs, setFaqs] = useState([]);
  useEffect(() => {
    api.get("/config").then((r) => setFaqs(r.data.faqs || []));
  }, []);
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-2 gap-8">
      <div className="rounded-3xl bg-gradient-to-br from-red-600 via-orange-500 to-amber-500 p-8 text-white flex flex-col justify-between min-h-[260px]">
        <div><Smartphone className="w-8 h-8 mb-3" /><h2 className="font-heading text-2xl sm:text-3xl font-extrabold">Android & iOS apps coming soon</h2><p className="text-white/90 mt-2">Same account, same coupons, deal alerts in your pocket.</p></div>
        <div className="flex gap-3 mt-6">
          <span className="inline-flex items-center gap-2 bg-black/80 rounded-xl px-4 py-2 text-sm font-semibold"><Play className="w-4 h-4" /> Google Play</span>
          <span className="inline-flex items-center gap-2 bg-black/80 rounded-xl px-4 py-2 text-sm font-semibold"><Apple className="w-4 h-4" /> App Store</span>
        </div>
      </div>
      <div data-testid="faq-section">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-3">FAQ</h2>
        <Accordion type="single" collapsible>
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`f${i}`}><AccordionTrigger data-testid={`faq-${i}`} className="text-left font-semibold">{f.q}</AccordionTrigger><AccordionContent className="text-slate-600">{f.a}</AccordionContent></AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

export default function Home() {
  const { radius } = useLoc();
  return (
    <div className="bg-white">
      <Navbar />
      <Hero />
      <Categories />
      <DealRow testid="row-500m" title="Deals within 500 metres" sub="Walk over in minutes" params={{ radius_km: 0.5, sort: "nearest" }} link="/deals?radius=0.5" />
      <DealRow testid="row-expiring" title="Expiring soon" sub="Grab them before the timer hits zero" params={{ radius_km: radius, sort: "expiring" }} link="/deals?sort=expiring" />
      <DealRow testid="row-trending" title="Trending nearby" params={{ radius_km: radius, sort: "popular" }} link="/deals?sort=popular" />
      <DealRow testid="row-new" title="Recently added" params={{ radius_km: radius, sort: "new" }} link="/deals?sort=new" />
      <FeaturedMerchants />
      <HowItWorks />
      <Packages />
      <AppsAndFaq />
      <Footer />
    </div>
  );
}
